import { hasUnsafeContent, hasProfanity } from '../../../utils/sanitize';

// Phone numbers are NOT country-specific: customers can be from any country, so we only check it
// looks like a phone number — an optional leading +, digits with the usual separators
// (space - . ( )), and 7–15 digits in total (the E.164 maximum).
const PHONE_CHARS = /^\+?[\d\s\-().]+$/;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

/**
 * Clean a typed/pasted phone value: Bangla (০-৯) and Arabic-Indic (٠-٩) digits → ASCII, and drop
 * zero-width / direction marks that copy-paste often drags in (they made valid numbers "invalid").
 * The form applies this as the user types, so what's saved is always ASCII digits.
 */
export const normalizePhoneInput = (value) =>
    String(value ?? '')
        .replace(/[০-৯]/g, (d) => String('০১২৩৪৫৬৭৮৯'.indexOf(d)))
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '');

const isValidPhone = (value) => {
    const s = normalizePhoneInput(value).trim();
    if (!PHONE_CHARS.test(s)) return false;
    const digits = s.replace(/\D/g, '').length;
    return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
};

// Format patterns per validation profile.
const PATTERNS = {
    name: /^[ঀ-৿A-Za-z.,:'’()/\s-]+$/,        // letters (bn/en), spaces & common punctuation . , : - ' ’ ( ) /
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    digits: /^\d+$/,
    alnum: /^[A-Za-z0-9\-/\s]+$/,                       // letters, digits, - / space
    year: /^(?:19|20)\d{2}$/,
    number: /^\d+(?:\.\d+)?$/,
};

const PROFILES_WITH_PATTERN = new Set([...Object.keys(PATTERNS), 'phone']);

// Field types that hold free text and therefore need safety/length/profanity checks.
const TEXTUAL_TYPES = new Set([undefined, 'text', 'tel', 'email', 'number', 'textarea']);

/**
 * Build an rc-field-form `rules` array for a customer field config.
 * Applies: required, max length, unsafe-content, profanity, and a format pattern.
 */
export function buildRules(field, t) {
    const rules = [];

    if (field.required) {
        const key = field.name === 'mobile_number' ? 'phoneRequired' : 'nameRequired';
        rules.push({ required: true, message: t(`sohozJob.${key}`) });
    }

    if (TEXTUAL_TYPES.has(field.type)) {
        const max = field.max ?? (field.type === 'textarea' ? 1000 : 120);
        rules.push({
            validator: (_, value) => {
                if (value == null || value === '') return Promise.resolve();
                const s = String(value);
                if (s.length > max) return Promise.reject(new Error(t('sohozJob.validation.tooLong', { max })));
                if (hasUnsafeContent(s)) return Promise.reject(new Error(t('sohozJob.validation.unsafe')));
                if (hasProfanity(s)) return Promise.reject(new Error(t('sohozJob.validation.profanity')));
                return Promise.resolve();
            },
        });
    }

    if (field.v === 'age') {
        rules.push({
            validator: (_, value) => {
                if (value == null || value === '') return Promise.resolve();
                const n = Number(value);
                return Number.isFinite(n) && n > 0 && n <= 120
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('sohozJob.validation.age')));
            },
        });
    } else if (PROFILES_WITH_PATTERN.has(field.v)) {
        rules.push({
            validator: (_, value) => {
                if (value == null || value === '') return Promise.resolve();
                const ok = field.v === 'phone' ? isValidPhone(value) : PATTERNS[field.v].test(String(value).trim());
                return ok
                    ? Promise.resolve()
                    : Promise.reject(new Error(t(`sohozJob.validation.${field.v}`)));
            },
        });
    }

    return rules;
}
